<script setup>
import { ref, watch, onUnmounted } from 'vue';

// 1. Assign Props
const props = defineProps({
  userLocation: {
    type: String,
    default: 'London'
  }
});

// 2. Reactive State
const locationName = ref('');
const formattedTime = ref('');
const formattedDate = ref('');
const timeZoneName = ref('');
const sunriseTime = ref('');
const sunsetTime = ref('');
const loading = ref(false);
const errorMessage = ref('');

// Interval handle for the live ticking clock
let clockInterval = null;
let currentTimeZone = null;

// 3. Clock Ticking Function
const updateClock = () => {
  if (!currentTimeZone) return;

  const now = new Date();

  // Format Time (HH:MM:SS) in local timezone
  formattedTime.value = new Intl.DateTimeFormat('en-GB', {
    timeZone: currentTimeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).format(now);

  // Format Date (e.g., Monday, 27 July 2026)
  formattedDate.value = new Intl.DateTimeFormat('en-GB', {
    timeZone: currentTimeZone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(now);
};

// Helper: Format ISO string to local HH:MM time
const formatSunTime = (isoString, timeZone) => {
  if (!isoString) return '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(new Date(isoString));
};

// Start or restart the 1-second interval ticker
const startClock = (timeZone) => {
  currentTimeZone = timeZone;
  if (clockInterval) clearInterval(clockInterval);
  updateClock();
  clockInterval = setInterval(updateClock, 1000);
};

// 4. Fetch Timezone Data & Sun Events
const fetchTimeZone = async () => {
  const input = props.userLocation ? props.userLocation.trim() : '';
  if (!input) return;

  loading.value = true;
  errorMessage.value = '';

  try {
    let lat, lon, displayName;
    const isPostcode = /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i.test(input);

    if (isPostcode) {
      const pcRes = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(input)}`);
      const pcData = await pcRes.json();
      if (pcData.status !== 200) throw new Error('Invalid UK Postcode');
      lat = pcData.result.latitude;
      lon = pcData.result.longitude;
      displayName = `${pcData.result.outward_code}, ${pcData.result.admin_district}`;
    } else {
      const geoRes = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(input)}&count=5&language=en&format=json`);
      const geoData = await geoRes.json();
      if (!geoData.results || geoData.results.length === 0) throw new Error('Town or location not found');

      const ukMatch = geoData.results.find(place => place.country_code === 'GB') || geoData.results[0];
      lat = ukMatch.latitude;
      lon = ukMatch.longitude;
      displayName = `${ukMatch.name}${ukMatch.admin1 ? ', ' + ukMatch.admin1 : ''}`;
    }

    // Lookup Timezone string AND daily Sunrise/Sunset via Open-Meteo
    const tzRes = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=sunrise,sunset&timezone=auto`
    );
    if (!tzRes.ok) throw new Error('Failed to fetch timezone data');

    const tzData = await tzRes.json();
    if (!tzData.timezone) throw new Error('Timezone data unavailable');

    locationName.value = displayName;
    timeZoneName.value = tzData.timezone;

    // Extract & Format today's Sunrise / Sunset times
    if (tzData.daily && tzData.daily.sunrise?.length && tzData.daily.sunset?.length) {
      sunriseTime.value = formatSunTime(tzData.daily.sunrise[0], tzData.timezone);
      sunsetTime.value = formatSunTime(tzData.daily.sunset[0], tzData.timezone);
    }

    // Start live clock with retrieved timezone
    startClock(tzData.timezone);

  } catch (err) {
    if (clockInterval) clearInterval(clockInterval);
    errorMessage.value = err.message || 'Error fetching time data';
  } finally {
    loading.value = false;
  }
};

// 5. Watchers & Cleanups
watch(
  () => props.userLocation,
  (newVal) => {
    if (newVal) fetchTimeZone();
  },
  { immediate: true }
);

// Clean up timer when component unmounts to prevent memory leaks
onUnmounted(() => {
  if (clockInterval) clearInterval(clockInterval);
});
</script>

<template>
  <div class="time-card">
    <!-- Loading State -->
    <div v-if="loading" class="state-message">
      Loading time for {{ userLocation }}...
    </div>

    <!-- Error State -->
    <div v-else-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <!-- Time Display -->
    <div v-else-if="formattedTime" class="time-info">
      <h2 class="location">Today</h2>
      <div class="clock-display">
        <i class="bi bi-clock-fill clock-icon"></i>
        <div class="digital-time">{{ formattedTime }}</div>
      </div>

      <div class="date-display">{{ formattedDate }}</div>

      <!-- Solar Information Row -->
      <div v-if="sunriseTime && sunsetTime" class="sun-events">
        <div class="sun-item">
          <i class="bi bi-sunrise-fill sun-icon sunrise"></i>
          <span>{{ sunriseTime }}</span>
        </div>
        <div class="sun-divider">•</div>
        <div class="sun-item">
          <i class="bi bi-sunset-fill sun-icon sunset"></i>
          <span>{{ sunsetTime }}</span>
        </div>
      </div>

      <div class="tz-badge">{{ timeZoneName }}</div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "../assets/styles/_variables.scss";

// Variables for easy thematic updates
$card-bg: #ffffff;
$primary-time: #0f172a;
$text-sub: #475569;
$text-muted: #64748b;
$sunrise-color: #f59e0b;
$sunset-color: #ea580c;

.time-card {
  background: $card-bg;
  padding: 1.25rem;
  border-radius: 16px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08);
  width: 100%;
  height: 250px;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  text-align: center;
  font-family: system-ui, -apple-system, sans-serif;
  margin: 0 auto;
  overflow: hidden;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
  }

  .time-info {
    width: 100%;
  }

  /* Status Messages */
  .state-message {
    color: $text-muted;
    font-size: 0.95rem;
  }

  .error-message {
    color: #e11d48;
    font-size: 0.95rem;
  }

  /* Clock Display */
  .clock-display {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin: 0.5rem 0 0.25rem 0;

    .clock-icon {
      font-size: 2rem;
      color: $dark-blue;
    }

    .digital-time {
      font-size: 2.5rem;
      font-weight: 700;
      color: $primary-time;
      letter-spacing: -0.02em;
      font-variant-numeric: tabular-nums;
    }
  }

  /* Date Display */
  .date-display {
    font-size: 1.5rem;
    font-weight: 500;
    color: $dark-pink;
  }

  /* Solar Information */
  .sun-events {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: 0.75rem;
    font-size: 1rem;
    font-weight: 600;
    color: #334155;

    .sun-item {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .sun-icon {
      font-size: 1.1rem;

      &.sunrise {
        color: $sunrise-color;
      }

      &.sunset {
        color: $sunset-color;
      }
    }

    .sun-divider {
      color: #cbd5e1;
    }
  }

  /* Timezone Badge */
  .tz-badge {
    display: inline-block;
    font-size: 0.75rem;
    font-weight: 600;
    color: $text-muted;
    background-color: #f1f5f9;
    padding: 3px 10px;
    border-radius: 20px;
    margin-top: 0.75rem;
  }
}
</style>